<template>
  <base-drawer :show="show" title="Create dashboard" @close="onClose">
    <drawer-section title="Templates" icon="icon-components">
      <div v-if="listLoading" class="widget-type-grid template-drawer-grid">
        <div v-for="n in 4" :key="'SKEL' + n" class="widget-type-card template-drawer-card template-drawer-card--skeleton">
          <span class="skel skel--dot"></span>
          <span class="skel skel--line-sm"></span>
          <span class="skel skel--text"></span>
        </div>
      </div>

      <div v-else class="widget-type-grid template-drawer-grid">
        <button
          type="button"
          class="widget-type-card template-drawer-card"
          :class="{ 'widget-type-card--active': !selected }"
          @click="selectBlank()"
        >
          <i class="tim-icons icon-simple-add"></i>
          <span>Blank dashboard</span>
          <small class="template-drawer-card-description">Start empty and add your own widgets</small>
        </button>
        <button
          v-for="t in templates"
          :key="'TPL' + t.id"
          type="button"
          class="widget-type-card template-drawer-card"
          :class="{ 'widget-type-card--active': selected && selected.id === t.id }"
          @click="select(t)"
        >
          <i class="tim-icons" :class="t.icon || 'icon-components'"></i>
          <span>{{ t.name }}</span>
          <small class="template-drawer-card-description">{{ t.description }}</small>
        </button>
      </div>
    </drawer-section>

    <drawer-section title="Details" icon="icon-align-left-2">
      <base-input required label="Name" v-model="name" placeholder="Dashboard name"></base-input>
      <base-textarea label="Description" :rows="3" placeholder="Description" v-model="description" />
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button
          type="primary"
          size="sm"
          :disabled="name.trim().length === 0 || loading"
          @click="onCreate"
        >
          {{ loading ? 'Creating...' : 'Create dashboard' }}
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue'

interface DashboardTemplate {
  id: number
  template_key: string
  name: string
  description: string
  icon: string
  visibility: 'all' | 'multi_group'
  widget_count: number
}

const props = defineProps<{
  show: boolean
  templates: DashboardTemplate[]
  loading?: boolean
  listLoading?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'create', payload: { templateId: number | null; name: string; description: string }): void
}>()

const selected = ref<DashboardTemplate | null>(null)
const name = ref('')
const description = ref('')

function select(t: DashboardTemplate) {
  selected.value = t
  name.value = t.name
  description.value = t.description || ''
}

function onClose() {
  emit('close')
}

function selectBlank() {
  selected.value = null
  name.value = ''
  description.value = ''
}

function onCreate() {
  if (name.value.trim().length === 0) return
  emit('create', { templateId: selected.value ? selected.value.id : null, name: name.value.trim(), description: description.value })
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.show) onClose()
}

watch(
  () => props.show,
  (visible) => {
    if (visible) {
      selected.value = null
      name.value = ''
      description.value = ''
      document.addEventListener('keydown', onEscKey)
    } else {
      document.removeEventListener('keydown', onEscKey)
    }
  }
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>

<style scoped>
/* Template cards carry a description, so they get their own layout: a two-column grid with
   uniform row heights, overriding the flex wrapping of the base widget-type-grid. */
.template-drawer-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: 1fr;
}
.template-drawer-grid .template-drawer-card {
  min-width: 0;
  min-height: 72px;
  padding: 8px 10px;
  gap: 3px;
  text-align: center;
  justify-content: center;
}
.template-drawer-grid .template-drawer-card i {
  font-size: 15px;
}
.template-drawer-grid .template-drawer-card > span:not(.badge) {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}
.template-drawer-card-description {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.55);
}
.white-content .template-drawer-grid .template-drawer-card > span:not(.badge) {
  color: rgba(0, 0, 0, 0.85);
}
.white-content .template-drawer-card-description {
  color: rgba(0, 0, 0, 0.55);
}
.template-drawer-card--skeleton {
  gap: 10px;
  pointer-events: none;
}
.template-drawer-card--skeleton .skel--line-sm {
  width: 55%;
}
.template-drawer-card--skeleton .skel--text {
  width: 80%;
}
.template-drawer-card-description {
  font-weight: 400;
  line-height: 1.3;
}
.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
